import { useGlobal } from '@common/core-layer/composables/useGlobal'
import { useLocationStore } from '@common/location-layer/store/location.store'
import { BaseService } from '../../http-layer/service/base.service'
import { getDeviceInfo } from '../../core-layer/utils'
import type {
  AdminV2PreviewSession,
  AuthResponse,
  CheckEmailPayload,
  CheckEmailResponse,
  CheckPasswordSetPayload,
  CheckPasswordSetResponse,
  ForgotPasswordPayload,
  GetMigrationStatusResponse,
  LoginWithAdminTokenV2Payload,
  LoginWithEmailPasswordPayload,
  LoginWithLoginCodePayload,
  LoginWithMagicLinkPayload,
  LoginWithSecureCodePayload,
  LoginWithSessionKeyPayload,
  LoginWithSocialIDPayload,
  MagicLinkLoginResponse,
  RefreshTokenPayload,
  RefreshTokenResponse,
  RequestMagicLinkPayload,
  ResetPasswordPayload,
  SendOtpPayload,
  SetNewPasswordPayload,
  SetPasswordPayload,
  SignUpPayload,
  UpdateSessionPayload,
  ValidateResetLinkPayload,
} from '../types'

const LOGIN_SIGNUP_HEADERS = { isIAM: 'true' }

class AuthService extends BaseService {
  private readonly locationId: string
  private readonly isGokollab: boolean
  private readonly isMeet: boolean

  constructor(locationId: string, baseURLKey: 'CLIENTPORTAL_SERVER_URL' | 'MEMBERSHIP_SERVER_URL' = 'CLIENTPORTAL_SERVER_URL') {
    const { isGokollab, isMeet } = useGlobal()

    // Defaults to client-portal; courses admin-preview IAM v2 exchange overrides to `MEMBERSHIP_SERVER_URL`.
    super({
      baseURL: baseURLKey,
      basePath: ``,
    })

    this.locationId = locationId
    this.isGokollab = isGokollab
    this.isMeet = isMeet
  }

  /**
   * Check if email exists
   */
  async checkEmail(payload: CheckEmailPayload): Promise<{ data: CheckEmailResponse }> {
    const { data } = await this.post<CheckEmailResponse>('/auth/check-email', {
      email: payload.email,
      locationId: payload.locationId,
    })
    return { data }
  }

  async getMigrationStatus(): Promise<{ data: GetMigrationStatusResponse }> {
    const { data } = await this.get<GetMigrationStatusResponse>('/v2/user/migration/status')
    return { data }
  }

  async checkIfPasswordSet(payload: CheckPasswordSetPayload): Promise<{ data: CheckPasswordSetResponse }> {
    const { data } = await this.post<CheckPasswordSetResponse>(`/v2/${payload.locationId}/users/check-password-set`, {
      locationId: payload.locationId,
      email: payload.email,
      userId: payload.userId,
      loginCode: payload.loginCode,
      token: payload.token,
    })
    return { data }
  }

  /**
   * Login with email and password
   */
  async loginWithEmailPassword(payload: LoginWithEmailPasswordPayload): Promise<{ data: AuthResponse }> {
    const { data } = await this.post<AuthResponse>(
      `/v2/${payload.locationId}/auth/login/email`,
      {
        email: payload.email,
        password: payload.password,
        userId: payload.userId || '',
        locationId: payload.locationId,
        redirectUrl: payload.redirectUrl || null,
        captchaToken: payload.captchaToken,
        ...getDeviceInfo(),
      },
      { headers: LOGIN_SIGNUP_HEADERS }
    )
    return { data }
  }

  /**
   * Login/Verify with secure code (OTP)
   */
  async verifyOTPForLogin(payload: LoginWithSecureCodePayload): Promise<{ data: AuthResponse }> {
    const { data } = await this.post<AuthResponse>(
      `/v2/${payload.locationId}/auth/login/otp`,
      {
        email: payload.email,
        otp: payload.otp,
        locationId: payload.locationId,
        token: payload.token,
        redirectUrl: payload.redirectUrl || null,
        action: payload.action || null,
        captchaToken: payload.captchaToken,
        ...getDeviceInfo(),
      },
      { headers: LOGIN_SIGNUP_HEADERS }
    )

    return { data }
  }

  /**
   * Login with social provider (Google, etc.)
   */
  async loginWithSocialID(payload: LoginWithSocialIDPayload): Promise<{ data: AuthResponse }> {
    const deviceInfo = getDeviceInfo()
    const { data } = await this.post<AuthResponse>(
      `/v2/${payload.locationId}/auth/login/socialLogin`,
      {
        email: payload.email,
        idToken: payload.idToken,
        userId: payload.userId,
        authProvider: payload.authProvider,
        fullName: payload.fullName,
        locationId: payload.locationId,
        redirectUrl: payload.redirectUrl || null,
        referredBy: payload.referredBy,
        signupPageUrl: payload.signupPageUrl || null,
        ...deviceInfo,
        domainName: payload.domainName || deviceInfo.domainName,
      },
      { headers: LOGIN_SIGNUP_HEADERS }
    )
    return { data }
  }

  /**
   * Sign up new user
   */
  async signUp(payload: SignUpPayload): Promise<{ data: AuthResponse }> {
    const { data } = await this.post<AuthResponse>(
      `/v2/${payload.locationId}/auth/signup`,
      {
        email: payload.email,
        password: payload.password,
        fullName: payload.username,
        locationId: payload.locationId,
        otp: payload.otp || null,
        token: payload.otpToken,
        redirectUrl: payload.redirectUrl || null,
        captchaToken: payload.captchaToken,
        referredBy: payload.referredBy,
        signupPageUrl: payload.signupPageUrl || null,
        ...(this.isGokollab ? {} : { appType: 'WL' }),
        ...getDeviceInfo(),
      },
      { headers: LOGIN_SIGNUP_HEADERS }
    )
    return { data }
  }

  /**
   * Send OTP to email
   */
  async sendOtp(payload: SendOtpPayload): Promise<{ data: { otpToken: string; isNewUser?: boolean } }> {
    const { data } = await this.post<{ otpToken: string; token?: string; isNewUser?: boolean }>(`/otps/${payload.locationId}/send`, {
      email: payload.email,
      locationId: payload.locationId,
      action: payload.action || null,
      captchaToken: payload.captchaToken,
      type: payload.type,
      fullName: payload.fullName,
      ...getDeviceInfo(),
    })
    return { data: { otpToken: this.isMeet ? data.token! : data.otpToken, isNewUser: data.isNewUser } }
  }

  /**
   * Set password (for new users or password reset).
   *
   * `options.authToken` (optional): explicit Bearer token for this request only.
   * Used by the deferred-login flow where the user is not yet marked
   * `isAuthenticated` in the store (so `BaseService` won't attach a token),
   * but the set-password endpoint still requires authorization. The token is
   * scoped to this single request and is NOT persisted into the auth store.
   */
  async setPassword(payload: SetPasswordPayload, options?: { authToken?: string }): Promise<{ data: AuthResponse }> {
    const headers: Record<string, string> = {}
    if (options?.authToken) {
      headers.Authorization = `Bearer ${options.authToken}`
    }
    const { data } = await this.post<AuthResponse>(
      `/v2/${payload.locationId}/auth/set-password`,
      {
        passwordResetToken: payload.passwordResetToken,
        contactId: payload.contactId,
        newPassword: payload.newPassword,
        confirmPassword: payload.confirmPassword,
        captchaToken: payload.captchaToken,
      },
      Object.keys(headers).length > 0 ? { headers } : undefined
    )
    return { data }
  }

  async setNewPassword(payload: SetNewPasswordPayload): Promise<{ data: AuthResponse }> {
    const { data } = await this.post<AuthResponse>(`/v2/${payload.locationId}/users/set-new-password`, {
      emailId: payload.emailId,
      newPassword: payload.newPassword,
      confirmPassword: payload.confirmPassword,
      loginCode: payload.loginCode,
      token: payload.token,
    })
    return { data }
  }

  /**
   * Forgot password - sends reset email
   */
  async forgotPassword(payload: ForgotPasswordPayload): Promise<{ data: { success: boolean } }> {
    const { data } = await this.post<{ success: boolean }>(`/v2/${payload.locationId}/auth/forgot-password`, {
      locationId: payload.locationId,
      email: payload.email,
      userId: payload.userId || '',
      captchaToken: payload.captchaToken,
      mode: payload.mode,
    })
    return { data }
  }

  /**
   * Validate reset password link (optional, for pre-submit validation)
   */
  async validateResetLink(payload: ValidateResetLinkPayload): Promise<{ data: { success: boolean } }> {
    const { data } = await this.post<{ success: boolean }>(`/v2/${payload.locationId}/users/validate-link`, {
      userId: payload.userId,
      token: payload.token,
      locationId: payload.locationId,
    })
    return { data }
  }

  /**
   * Reset password with token
   */
  async resetPassword(payload: ResetPasswordPayload): Promise<{ data: { success: boolean } }> {
    const { data } = await this.post<{ success: boolean }>(`/v2/${payload.locationId}/auth/reset-password`, {
      userId: payload.userId,
      token: payload.token,
      password: payload.password,
      locationId: payload.locationId,
      isPwa: false,
      revokeAllSessions: payload.revokeAllSessions,
    })
    return { data }
  }

  /**
   * Login with magic link login code (email link code)
   */
  async loginWithLoginCode(payload: LoginWithLoginCodePayload): Promise<{ data: AuthResponse }> {
    const { data } = await this.post<AuthResponse>(
      `/v2/${payload.locationId}/auth/login/loginCode`,
      {
        ...getDeviceInfo(),
        email: payload.email,
        loginCode: payload.loginCode,
        locationId: payload.locationId,
        domainName: payload.domainName,
        redirectUrl: payload.redirectUrl ?? null,
      },
      { headers: LOGIN_SIGNUP_HEADERS }
    )
    return { data }
  }

  async loginWithSessionKey(payload: LoginWithSessionKeyPayload): Promise<{ data: AuthResponse }> {
    const { data } = await this.post<AuthResponse>(
      `/v2/${payload.locationId}/auth/login/sessionKey`,
      {
        ...getDeviceInfo(),
        email: payload.email,
        sessionKey: payload.sessionKey,
        locationId: payload.locationId,
        domainName: payload.domainName,
        redirectUrl: payload.redirectUrl ?? null,
      },
      { headers: LOGIN_SIGNUP_HEADERS }
    )
    return { data }
  }

  /**
   * Login with magic link token.
   * May return an OTP handoff when the magic-link token is expired.
   */
  async loginWithMagicLink(payload: LoginWithMagicLinkPayload): Promise<{ data: MagicLinkLoginResponse }> {
    const { data } = await this.post<MagicLinkLoginResponse>(
      `/v2/${payload.locationId}/auth/login/magic-link-token`,
      {
        ...getDeviceInfo(),
        email: payload.email,
        token: payload.token,
        domainName: payload.domainName,
        locationId: payload.locationId,
        redirectUrl: payload.redirectUrl ?? null,
        ...(payload.otpHandoffOnExpire === true ? { otpHandoffOnExpire: true } : {}),
        ...(payload.eventNotificationOtpRecovery === true ? { eventNotificationOtpRecovery: true } : {}),
      },
      { headers: LOGIN_SIGNUP_HEADERS }
    )
    return { data }
  }

  async requestMagicLink(payload: RequestMagicLinkPayload): Promise<{ data: { success: boolean } }> {
    const { data } = await this.post<{ success: boolean }>(
      `/${payload.locationId}/tokens/request-magic-link`,
      {
        token: payload.token,
        locationId: payload.locationId,
        source: payload.source,
      },
      { headers: LOGIN_SIGNUP_HEADERS }
    )
    return { data }
  }

  /**
   * Refresh authentication token.
   *
   * Meet app + Auth V1 (`isAuthV2Enabled === false`) additionally needs the refresh token
   * passed as a `token` request header — the clientportal backend reads it from there, not the body.
   */
  async refreshToken(payload: RefreshTokenPayload): Promise<{ data: RefreshTokenResponse }> {
    try {
      const headers: Record<string, string> = { ...LOGIN_SIGNUP_HEADERS }
      try {
        const locationStore = useLocationStore()
        if (this.isMeet && !locationStore.isAuthV2Enabled && payload.refreshToken) {
          headers.token = payload.refreshToken
        }
      } catch {
        // location store unavailable (e.g. nitro context outside composable setup); skip silently
      }

      const { data } = await this.post<RefreshTokenResponse>(
        `/v2/${payload.locationId}/auth/refresh-token`,
        { refreshToken: payload.refreshToken },
        { headers }
      )
      return { data }
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Token refresh failed'
      console.error('Error refreshing token:', errorMessage)
      throw error
    }
  }

  /**
   * IAM v2 courses admin-preview token exchange (mirrors membership-hmi-preview PR #1321 `signInAsAdminV2`).
   * Exchanges the `v2.`-prefixed builder token for a location-scoped IAM v2 admin token, sent as both
   * body `token` and `Authorization: Bearer` header (IAM gateway needs the header for courses.write; the
   * body value is forwarded to client-portal). No Firebase sign-in here.
   *
   * Requires `AuthService` constructed with `MEMBERSHIP_SERVER_URL` — verified `POST /membership/user/v2/login-token`
   * -> 200; clientclub base or plural `/memberships` path return 404/401.
   */
  async loginWithAdminTokenV2(payload: LoginWithAdminTokenV2Payload): Promise<{ data: AdminV2PreviewSession }> {
    const { data } = await this.post<AdminV2PreviewSession>(
      '/user/v2/login-token',
      {
        locationId: payload.locationId,
        token: payload.agencyToken,
        // Forwarded to membership-backend for the agency-user email + company fallback lookup
        // (mirrors membership-hmi-preview PR #1329). Omitted from the JSON body when falsy.
        companyId: payload.companyId || undefined,
      },
      {
        headers: { Authorization: `Bearer ${payload.agencyToken}` },
        // Hits `/membership/user/v2/login-token` directly (verified 200); otherwise clientportal rewrite
        // proxies to the plural `/clientportal-middleware/memberships/...` path, which 404s for this endpoint.
        skipClientPortalMiddleware: true,
      }
    )
    return { data }
  }

  /**
   * generate IAM_V2 token using firebase auth
   */
  async generateIAMTokenFromFirebaseToken(firebaseAuthToken: string): Promise<{ token: string; refreshToken: string }> {
    const { data } = await this.post<{ token: string; refreshToken: string }>('/auth/generate-iam-v2-token', undefined, {
      headers: { 'token-id': firebaseAuthToken },
    })
    return data
  }

  async updateSession(payload: UpdateSessionPayload): Promise<void> {
    const body = {
      loggedIn: false,
      pushToken: payload.pushToken ?? '',
      brandedAppId: payload.brandedAppId ?? '',
    }
    if (payload.isV2) {
      await this.patch(`/v2/${payload.locationId}/auth/session/${payload.sessionUniqueKey}`, body)
    } else {
      await this.put('/auth/session', body)
    }
  }
}

// Export class for instantiation when needed
export { AuthService }
